/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Focused Practice Empty State Component - Compelling empty state for 15-18 year olds
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import PropTypes from 'prop-types'
import { Target, BarChart3, Zap, TrendingUp, ArrowRight } from 'lucide-react'

const FocusedPracticeEmptyState = ({ onStartChallenge }) => {
  const steps = [
    {
      number: '1',
      icon: Target,
      title: 'Take a Challenge',
      description:
        'Complete a practice test. This shows us exactly where you stand - your strengths and the areas that need work.',
      color: 'primary'
    },
    {
      number: '2',
      icon: BarChart3,
      title: 'We Analyze Your Results',
      description:
        'Our system identifies specific skills where you scored below 80%. No guessing - just data.',
      color: 'purple'
    },
    {
      number: '3',
      icon: Zap,
      title: 'Get Custom Practice',
      description:
        'You\'ll see a "Practice" button on underperforming domains. Click it for personalized drills built just for you.',
      color: 'amber'
    },
    {
      number: '4',
      icon: TrendingUp,
      title: 'Track Your Growth',
      description:
        'Every session shows before vs. after scores. Watch those numbers climb with each practice round.',
      color: 'green'
    }
  ]

  const colorClasses = {
    primary: {
      bg: 'bg-primary-100 dark:bg-primary-900/30',
      icon: 'text-primary-600 dark:text-primary-400',
      border: 'border-primary-200 dark:border-primary-800',
      number: 'bg-primary-600 text-white'
    },
    purple: {
      bg: 'bg-purple-100 dark:bg-purple-900/30',
      icon: 'text-purple-600 dark:text-purple-400',
      border: 'border-purple-200 dark:border-purple-800',
      number: 'bg-purple-600 text-white'
    },
    amber: {
      bg: 'bg-amber-100 dark:bg-amber-900/30',
      icon: 'text-amber-600 dark:text-amber-400',
      border: 'border-amber-200 dark:border-amber-800',
      number: 'bg-amber-600 text-white'
    },
    green: {
      bg: 'bg-green-100 dark:bg-green-900/30',
      icon: 'text-green-600 dark:text-green-400',
      border: 'border-green-200 dark:border-green-800',
      number: 'bg-green-600 text-white'
    }
  }

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Hero Section */}
      <div className="relative px-6 pt-10 pb-8 sm:px-10 text-center">
        {/* Animated background circles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-100 dark:bg-primary-900/20 rounded-full opacity-50 animate-pulse" />
          <div
            className="absolute -bottom-10 -left-10 w-32 h-32 bg-purple-100 dark:bg-purple-900/20 rounded-full opacity-50 animate-pulse"
            style={{ animationDelay: '1s' }}
          />
        </div>

        {/* Icon */}
        <div className="relative inline-flex items-center justify-center w-20 h-20 mb-6 rounded-2xl bg-gradient-to-br from-primary-500 to-purple-600 shadow-lg">
          <Target className="w-10 h-10 text-white" />
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
            <Zap className="w-3 h-3 text-white" />
          </div>
        </div>

        {/* Headlines */}
        <h2 className="relative text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
          Your Personal Training Ground
        </h2>
        <p className="relative text-lg text-primary-600 dark:text-primary-400 font-medium mb-4">
          You're looking to level up? We respect that.
        </p>
        <p className="relative text-gray-600 dark:text-gray-400 max-w-xl mx-auto">
          Focused Practice isn't random busywork. It's targeted training based on YOUR actual
          performance. Smart students don't just work harder - they work smarter.
        </p>
      </div>

      {/* How It Works Section */}
      <div className="px-6 pb-10 sm:px-10">
        <h3 className="text-center text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-8">
          How It Works
        </h3>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((step, index) => {
            const colors = colorClasses[step.color]
            const Icon = step.icon
            return (
              <div
                key={step.number}
                className={`relative p-5 rounded-xl ${colors.bg} border ${colors.border} transition-transform hover:scale-[1.02]`}
              >
                {/* Step number */}
                <div
                  className={`absolute -top-3 -left-2 w-7 h-7 ${colors.number} rounded-full flex items-center justify-center text-sm font-bold shadow-md`}
                >
                  {step.number}
                </div>

                {/* Icon */}
                <div className="mb-3 pt-1">
                  <Icon className={`w-6 h-6 ${colors.icon}`} />
                </div>

                {/* Content */}
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {step.title}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {step.description}
                </p>

                {/* Connector arrow (hidden on last item and mobile) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Motivation Quote */}
        <div className="mt-8 p-4 bg-gray-100 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-center">
          <p className="text-gray-700 dark:text-gray-300 italic">
            "The best athletes don't just practice more — they practice smarter. Why waste time on
            stuff you already know?"
          </p>
        </div>

        {/* CTA Section */}
        <div className="mt-8 text-center">
          <button
            onClick={onStartChallenge}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all"
          >
            <Target className="w-5 h-5" />
            Take a Challenge to Get Started
          </button>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Your focused practice sessions will appear here once you complete a challenge
          </p>
        </div>
      </div>
    </div>
  )
}

FocusedPracticeEmptyState.propTypes = {
  /** Callback function triggered when user clicks the CTA button to start a challenge */
  onStartChallenge: PropTypes.func.isRequired
}

export default FocusedPracticeEmptyState
